import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import axioInstance from "@/libs/axios";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import { RootState } from "@/store/root-reducer";
import { QuoteData } from "@/types/quote/quote-data";
import handleError, { ErrorResponse } from "@/utils/handle-errors";
import { normalizePostageOption } from "@/utils/review.utils";

export const useSubmitReview = (
  {
    version,
    type,
    attributes,
    referenceId,
    viewPage,
  }: {
    version: string | undefined;
    type: string;
    attributes: QuoteData["attributes"];
    referenceId: string | undefined;
    viewPage: string;
  },
  setButtonDisabled: (boolean: boolean) => void
) => {
  const [newQuote, setNewQuote] = useState<QuoteData>();
  const payOption = useSelector((state: RootState) => state.paymentConsents);
  const dispatch = useDispatch();
  const router = useRouter();

  const { autoPaySelected, setIsOpenCardholderConsentModal } =
    useRenewalPaymentSummaryStateContext();
  const submitReview: () => Promise<void> = async () => {
    if (version) {
      try {
        const requestBody = {
          type,
          channel: "ONLINE",
          version,
          viewPage,
          attributes: {
            domain: attributes.domain,
            paymentDetails: {
              agreeTermsAndConditions:
                attributes?.paymentDetails?.agreeTermsAndConditions,
              agreeKeyFactAndTOB:
                attributes?.paymentDetails?.agreeKeyFactAndTOB,
              optOutAutoRenewal: attributes?.paymentDetails?.optOutAutoRenewal,
              postageOption: normalizePostageOption(
                attributes?.paymentDetails?.postageOption
              ),
            },
            paymentOption: {
              selectedPaymentOption: payOption.type,
              autoPaySelected,
            },
          },
        };
        const config = {
          data: requestBody,
        };
        const response = await axioInstance.patch(
          `/quote/${referenceId}/quoteReviewSubmit`,
          config
        );
        setNewQuote(response.data.data);
        setIsOpenCardholderConsentModal(false);
      } catch (error) {
        setIsOpenCardholderConsentModal(false);
        const axiosError = error as AxiosError;
        handleError(axiosError as AxiosError<ErrorResponse>, router, dispatch);
      } finally {
        setButtonDisabled(false);
      }
    } else {
      setButtonDisabled(false);
    }
  };

  return [newQuote, submitReview];
};
